/**
 * PACKS — Flowhub storefront client (browser, zero dependencies)
 * ---------------------------------------------------------------
 * Talks to the Cloudflare Worker proxy (never Flowhub directly), so no
 * credentials ever touch the browser.
 *
 *   const packs = new PacksFlowhub({
 *     baseUrl: "https://packs-flowhub-proxy.<sub>.workers.dev",
 *     store: "sgv",        // storeKey the proxy maps to a token
 *     license: "rec",      // "rec" | "med"
 *   });
 *   const { products, total, aggregations } = await packs.listProducts({ limit: 24 });
 *   await packs.addItem(variantId, 1);
 *   location.href = await packs.checkoutUrl();
 *
 * Cart id is persisted in localStorage (per store + license) so the cart
 * survives page reloads.
 */
(function (global) {
  "use strict";

  class PacksError extends Error {
    constructor(code, message, detail) {
      super(message);
      this.code = code;
      this.detail = detail;
    }
  }

  class PacksFlowhub {
    constructor(opts = {}) {
      if (!opts.baseUrl) throw new PacksError("config", "baseUrl (the Worker URL) is required");
      this.base = String(opts.baseUrl).replace(/\/+$/, "");
      this.store = String(opts.store || "sgv").toLowerCase();
      this.license = String(opts.license || "rec").toLowerCase(); // rec | med
      this.cartKey = `packs_cart_${this.store}_${this.license}`;
      // Debug logging: pass { debug:true } or add ?debug=1 to the page URL.
      this.debug = !!opts.debug || (typeof location !== "undefined" && /[?&]debug=1/.test(location.search));
    }

    get _cartType() { return this.license === "med" ? "MED" : "REC"; }

    // ---- low-level request ------------------------------------------------
    async _request(method, apiPath, { params, body } = {}) {
      let qs = "";
      if (params) {
        const sp = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") sp.append(k, v); });
        qs = sp.toString() ? "?" + sp.toString() : "";
      }
      const url = `${this.base}/store/${this.store}${apiPath}${qs}`;
      let res;
      try {
        res = await fetch(url, {
          method,
          headers: { "Content-Type": "application/json" },
          body: body ? JSON.stringify(body) : undefined,
        });
      } catch (e) {
        throw new PacksError("network", "Couldn't reach the store. Check your connection and try again.", e);
      }
      if (res.status === 429) {
        throw new PacksError("rate_limit", "The store is busy. Please try again in a moment.", { retryAfter: res.headers.get("Retry-After") });
      }
      let json = {};
      try { json = await res.json(); } catch (_) { /* non-JSON */ }
      const err = json && json.errors && json.errors.length ? json.errors[0] : null;
      if (this.debug) console.log("[PacksFlowhub]", method, apiPath, "req>", (body && JSON.stringify(body)) || "", "resp>", res.status, json);
      if (!res.ok || err) {
        // Always log failures with Flowhub's full response so the real cause is visible.
        console.error("[PacksFlowhub]", method, apiPath, "->", res.status, "|", (err && err.message) || res.statusText, "| body sent:", body || null, "| response:", json);
        throw new PacksError((err && err.code) || `http_${res.status}`, (err && err.message) || res.statusText || "Request failed", json);
      }
      return json.data;
    }

    // ---- catalog ----------------------------------------------------------
    /**
     * List products. Returns { products, total, aggregations }.
     * Products are deduped by variant and filtered to the active license, since
     * Flowhub returns a MED and a REC row (different pricing) per variant.
     */
    async listProducts({ limit = 24, offset = 0, orderBy, orderDir, filter, productId } = {}) {
      const data = await this._request("GET", "/v3/products", {
        params: { limit, offset, medrec: this.license, order_by: orderBy, order_dir: orderDir, filter, product_id: productId },
      });
      const raw = (data && data.products) || [];
      const wanted = this._cartType; // "REC" | "MED"
      const seen = new Set();
      const products = [];
      for (const p of raw) {
        const lic = Array.isArray(p.med_or_rec) ? p.med_or_rec : [];
        if (lic.length && !lic.includes(wanted)) continue;   // wrong license row
        if (seen.has(p.variant_id)) continue;                 // duplicate
        seen.add(p.variant_id);
        products.push(p);
      }
      return { products, total: (data && data.total) || products.length, aggregations: (data && data.aggregations) || {} };
    }

    getProduct(productId) { return this._request("GET", `/v3/products/${productId}`); }

    /** All in-license variants for one product (used by the PDP). Reuses the
     *  catalog shape (full variant objects) rather than the detail endpoint. */
    async productVariants(productId) {
      // Flowhub's product_id query filter is unreliable, so filter client-side too.
      const { products } = await this.listProducts({ productId, limit: 100 });
      const exact = products.filter((p) => p.product_id === productId);
      if (exact.length) return exact;
      // Not on the returned page — fall back to the single-product detail endpoint.
      try {
        const d = await this.getProduct(productId);
        const arr = Array.isArray(d) ? d : (d && (d.variants || d.products)) || (d ? [d] : []);
        const want = this._cartType;
        return arr.filter((p) => !Array.isArray(p.med_or_rec) || !p.med_or_rec.length || p.med_or_rec.includes(want));
      } catch (e) { return []; }
    }

    // ---- cart -------------------------------------------------------------
    getStoredCartId() { try { return localStorage.getItem(this.cartKey); } catch (_) { return null; } }
    _storeCartId(id) { try { id ? localStorage.setItem(this.cartKey, id) : localStorage.removeItem(this.cartKey); } catch (_) {} }

    async ensureCart() {
      const existing = this.getStoredCartId();
      if (existing) {
        try { await this.getCart(existing); return existing; }
        catch (_) { this._storeCartId(null); } // stale/expired — make a new one
      }
      // `type` (REC/MED) is required by Flowhub. Send it as query AND body so
      // it lands regardless of how the endpoint reads it.
      const cart = await this._request("POST", "/v3/carts", { params: { type: this._cartType }, body: { type: this._cartType } });
      const id = cart && (cart.cart_id || cart.id);
      if (!id) throw new PacksError("cart", "Could not create a cart");
      this._storeCartId(id);
      return id;
    }

    async getCart(id) {
      id = id || this.getStoredCartId();
      if (!id) return null;
      return this._request("GET", `/v3/carts/${id}`);
    }

    /** Add (or, with an absolute quantity, set) an item. quantity 0 removes it. */
    async setItem(variantId, quantity) {
      const id = await this.ensureCart();
      // `type` (REC/MED) must be a QUERY param here (the body value is ignored),
      // same as cart creation.
      return this._request("PUT", `/v3/carts/${id}/item`, { params: { type: this._cartType }, body: { variant: variantId, quantity: Number(quantity) } });
    }
    addItem(variantId, quantity = 1) { return this.setItem(variantId, quantity); }
    removeItem(variantId) { return this.setItem(variantId, 0); }

    applyPromo(code) {
      return this.ensureCart().then((id) => this._request("PUT", `/v3/carts/${id}/promo`, { params: { type: this._cartType }, body: { promo_code: code } }));
    }

    /** Get the Flowhub-hosted checkout URL and (caller) redirect to it. */
    async checkoutUrl(redirectUrl) {
      const id = this.getStoredCartId();
      if (!id) throw new PacksError("empty_cart", "Your cart is empty.");
      const data = await this._request("GET", `/v3/carts/${id}/submit`, {
        params: { redirect_url: redirectUrl || location.href },
      });
      return typeof data === "string" ? data : (data && (data.checkout_url || data.url)) || data;
    }

    clearCart() { this._storeCartId(null); }

    /**
     * Call once on page load: if Flowhub redirected back with fh_order_id the
     * order succeeded, so clear the local cart. Returns the order id if present.
     */
    handleReturn() {
      const p = new URLSearchParams(location.search);
      const orderId = p.get("fh_order_id");
      if (orderId) this.clearCart();
      return orderId;
    }

    // ---- presentation helpers (static) ------------------------------------
    static money(cents) { return cents == null ? "" : "$" + (Number(cents) / 100).toFixed(2); }

    static image(p, size = "thumbnail") {
      const set = p && Array.isArray(p.product_web_images) && p.product_web_images[0];
      if (set) {
        const pick = set[size] || set.thumbnail || set.mobile || set.tablet || set.desktop || set.original;
        if (pick && pick.url) return pick.url;
      }
      return (p && p.image_url) || "";
    }

    static thc(p) {
      const arr = (p && p.potencies) || [];
      const hit = arr.find((x) => /total\s*%?\s*thc/i.test(x.name)) || arr.find((x) => /(^|\s)%?\s*thc\b/i.test(x.name));
      if (!hit) return "";
      const n = parseFloat(hit.value);
      return isNaN(n) ? "" : `${n.toFixed(1)}% THC`;
    }

    static sizeLabel(p) {
      if (p && p.variant_name) return p.variant_name;
      if (p && p.weight_volume) {
        const uom = (p.weight_volume_uom || "").toLowerCase().startsWith("gram") ? "g" : (p.weight_volume_uom || "");
        return `${parseFloat(p.weight_volume)}${uom}`.trim();
      }
      return "";
    }

    static isOnSale(p) {
      return p && p.post_tax_discounted_price != null && p.post_tax_discounted_price < p.post_tax_price;
    }
    static price(p) { return PacksFlowhub.money(PacksFlowhub.isOnSale(p) ? p.post_tax_discounted_price : p.post_tax_price); }
  }

  PacksFlowhub.Error = PacksError;
  global.PacksFlowhub = PacksFlowhub;
})(typeof window !== "undefined" ? window : this);